<script setup lang="ts">
import CloseIcon from '@/assets/icons/cross-icon.svg?raw';
import model from '@/ts/model.ts';
import { getScript } from '@/ts/utils/model-utils';
import { onUnmounted, ref, watchEffect } from 'vue';

const MAX_RESET_ATTEMPTS = 6;

let widgetId: string | null = null;
let tInit = 0;
let tReset = 0;
let resetAttempts = 0;

const errorMsg = ref<string>('');

function onClose(){
    delete model.turnstile;

    if(widgetId) {
        window.turnstile?.remove(widgetId);
        widgetId = null;
    }

    errorMsg.value = '';
    resetAttempts = 0;

    clearTimeout(tInit);
    clearTimeout(tReset);
}

function onManualClose(){
    model.turnstile?.userCancelCallback?.();
    onClose();
}

onUnmounted(onClose);

watchEffect(async () => {
    const data = model.turnstile;
    const sitekey = data?.sitekey;

    clearTimeout(tInit);
    clearTimeout(tReset);

    errorMsg.value = '';

    if(widgetId) {
        window.turnstile?.remove(widgetId);
        widgetId = null;
    }

    if(!sitekey) return;

    const scriptUrl = model.clientConfig.scripts?.turnstile;
    if(!scriptUrl) {
        console.error("Turnstile script URL is not configured.");
        return;
    }

    await getScript(scriptUrl, true);

    // model.turnstile may have changed (or been cleared) while the script was loading.
    if(model.turnstile?.sitekey !== sitekey) return;

    const instance = window.turnstile;
    if(!instance) return;

    tInit = setTimeout(() => {
        resetAttempts = 0;

        widgetId = instance.render("#turnstile-container", {
            sitekey,
            callback: (token: string) => {
                if(model.turnstile) delete model.turnstile.userCancelCallback;
                model.turnstile?.successCallback?.(token);
                setTimeout(onClose, 2000);
            },
            "error-callback": (errorCode: string) => {
                console.error("Turnstile error: ", errorCode);
                errorMsg.value = `Turnstile error: ${errorCode}, resetting...`;
                onWidgetReset();
            },
            "expired-callback": () => {
                errorMsg.value = `Token expired, resetting...`;
                onWidgetReset();
            },
            "timeout-callback": () => {
                errorMsg.value = `Challenge timed out, resetting...`;
                onWidgetReset();
            },
        });
    }, 500);
});

function onWidgetReset(){
    const instance = window.turnstile;
    if(!widgetId || !instance) return;

    clearTimeout(tReset);

    if(resetAttempts >= MAX_RESET_ATTEMPTS){
        instance.remove(widgetId);
        widgetId = null;
        errorMsg.value = 'Too many failed attempts, please close this window and try again.';
        return;
    }

    resetAttempts++;
    instance.reset(widgetId);

    tReset = setTimeout(() => {
        errorMsg.value = '';
    }, 3000);
}
</script>

<template>
    <div id="turnstile-container-outer">
        <div id="turnstile-container-inner">
            <div class="header">
                <button type="button" class="close" v-html="CloseIcon" @click.prevent.stop="onManualClose"></button>
            </div>
            <div id="turnstile-container"></div>
            <div class="error-msg">
                {{ errorMsg }}
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    #turnstile-container-outer{
        position: fixed;
        z-index: 290;
        background-color: #00000056;
        top:0;
        left:0;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
    }

    #turnstile-container-inner{
        position: relative;
        background-color: #fff;
        min-width: 300px;
        min-height: 300px;
        & > .header{
            display: flex;
            justify-content: flex-end;
            & > .close{
                position: relative;
                display: flex;
                justify-content: center;
                align-items: center;
                background: transparent;
                border: none;
                padding: 0;
                margin: 0;
                cursor: pointer;
                &,
                & > *{
                    width: 30px;
                    height: 30px;
                    color: #000;
                }
            }
        }
        & .error-msg{
            text-align: center;
            color: rgb(214, 0, 0);
            padding: 5px;
        }
    }
</style>